// Thin wrapper over aws-rum-web's AwsRum client (CloudWatch RUM), installed
// as an npm dependency rather than AWS's CDN <script> snippet so it stays
// covered by the existing `script-src 'self'` CSP - see
// infra/lib/kanalearn-rum-stack.ts for the App Monitor/Identity Pool this
// talks to.
//
// Safe no-op (never constructs an AwsRum client) unless all three of
// REACT_APP_RUM_APP_MONITOR_ID / REACT_APP_RUM_IDENTITY_POOL_ID /
// REACT_APP_RUM_REGION are set at build time, exactly like ga4.ts/posthog.ts
// no-op without their own config. scripts/check-env.js warns when they are
// missing from a build, since RUM then stays silently off.
//
// Telemetry collected: page views, performance metrics, JS errors and HTTP
// errors, plus RUM's own automatic session/device/browser collection -
// exactly the `telemetries` set configured server-side in
// appMonitorConfiguration (kanalearn-rum-stack.ts). On top of that, every
// custom product event already sent through ../index.ts's track() (e.g.
// lesson_started, quiz_completed) is also forwarded here via recordEvent()
// - see track() in ../index.ts.
//
// Page views specifically are NOT wired through the page()/track() facade
// in ../index.ts: aws-rum-web patches history.pushState/replaceState and
// listens for popstate by default (`disableAutoPageView` defaults to
// false), so it already observes every react-router-dom navigation on its
// own. Calling recordPageView() from PageViewTracker too would double-count
// every route change, so ../index.ts's page()/identify()/reset()
// intentionally never call into this module for that.
//
// Auth: only identityPoolId is configured below, no guestRoleArn (there's
// no such output from kanalearn-rum-stack.ts). aws-rum-web only uses the
// classic AssumeRoleWithWebIdentity flow (which needs guestRoleArn) when
// both identityPoolId AND guestRoleArn are present; with identityPoolId
// alone it falls back to the enhanced GetCredentialsForIdentity flow, which
// resolves credentials via whichever role the identity pool already has
// attached - exactly the unauthenticated role kanalearn-rum-stack.ts wires
// up with CfnIdentityPoolRoleAttachment.
//
// Consent: the client is NOT constructed until measurement consent is
// granted. Constructing it is itself a side effect that no config flag
// prevents - the moment the object exists it calls Cognito Identity
// (GetId + GetCredentialsForIdentity, creating an anonymous identity and
// caching credentials in localStorage) and, with allowCookies, writes the
// cwr_s/cwr_u cookies, because it builds its event cache before applying
// `enableRumClient: false` / disable(). All of that was observed in a real
// browser before any consent. So init() only arms the module, and
// setConsent(true) builds the client on first grant. Withdrawal disables it,
// switches cookies off and purges the ones it wrote. The cost is that a
// first-time visitor's initial page load is not measured, which is correct:
// nothing may be collected before they consent.
import { AwsRum } from "aws-rum-web";
import type { AwsRumConfig } from "aws-rum-web";

const APP_MONITOR_ID = process.env.REACT_APP_RUM_APP_MONITOR_ID;
const IDENTITY_POOL_ID = process.env.REACT_APP_RUM_IDENTITY_POOL_ID;
const REGION = process.env.REACT_APP_RUM_REGION;

// Informational only (shown in the CloudWatch RUM console) - not tied to
// package.json's version since that isn't exposed to the frontend bundle.
const APPLICATION_VERSION = "1.0.0";

let client: AwsRum | undefined;
let armed = false;
let cookiesAllowed = false;

// Called once from src/index.tsx (via initRum()). Deliberately does not build
// the client - see the Consent note above.
export function init(): void {
    armed = true;
}

function buildClient(): void {
    if (client || !armed) return;
    if (!APP_MONITOR_ID || !IDENTITY_POOL_ID || !REGION) return;

    // Keep sessionSampleRate/telemetries/allowCookies/enableXRay in sync
    // with appMonitorConfiguration in infra/lib/kanalearn-rum-stack.ts - the
    // npm-installed client doesn't fetch that config remotely, it has to be
    // duplicated here.
    const config: AwsRumConfig = {
        sessionSampleRate: 0.1,
        identityPoolId: IDENTITY_POOL_ID,
        endpoint: `https://dataplane.rum.${REGION}.amazonaws.com`,
        telemetries: ["errors", "performance", "http"],
        allowCookies: true,
        enableXRay: false,
    };

    try {
        client = new AwsRum(APP_MONITOR_ID, APPLICATION_VERSION, REGION, config);
    } catch (error) {
        // Mirrors AWS's own documented setup snippet: never let a RUM
        // client failure (e.g. blocked by an ad blocker, malformed config)
        // break the app.
        console.warn("[analytics] CloudWatch RUM failed to initialize", error);
    }
}

// Called from ../index.ts's onConsentChange handler (and once immediately
// from initRum() to apply whatever consent state is already known),
// mirroring posthogProvider.setConsent(). A no-op if RUM was never armed or
// its env vars are unset.
export function setConsent(granted: boolean): void {
    if (granted) {
        buildClient();
        if (!client) return;
        client.allowCookies(true);
        client.enable();
        cookiesAllowed = true;
        return;
    }

    if (!client) return;
    client.disable();
    client.allowCookies(false);
    // Withdrawal: remove the session/user cookies and cached credentials this
    // client wrote. Only when they could exist.
    if (cookiesAllowed) client.clearCookies();
    cookiesAllowed = false;
}

// Called from ../index.ts's track() so every product event that already
// reaches GA4/PostHog also reaches CloudWatch RUM as a custom event, via
// AwsRum/Orchestration's own recordEvent(eventType, eventData, metadata?)
// method - a no-op until consent has built the client.
export function recordEvent(name: string, properties: Record<string, unknown> = {}): void {
    if (!client) return;
    try {
        client.recordEvent(name, properties);
    } catch (error) {
        console.warn("[analytics] CloudWatch RUM failed to record event", name, error);
    }
}

// Test-only escape hatch, mirrors the pattern in consent.ts.
export function __getClientForTests(): AwsRum | undefined {
    return client;
}
